import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from './quiz/ConfirmDialog';
import { SoundMuteToggle } from './quiz/QuizAtoms';
import { playSelect } from '../utils/sound';
import logoUrl from '../assets/images/cleveroo-logo.png';
import './FillInTheBlank.css';

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

interface ClozePassage {
  title?: string;
  template?: string;  // text with %% markers for each blank
}

interface FIBQuestionContent {
  responseFormat?: string;       // 'text'
  passage?: ClozePassage;
  passageId?: string;
  blankIndex?: number;           // 0-based position in the template
  cue?: string;                  // base-word chip shown after the blank
  hint?: string;                 // revealed on demand
  [key: string]: unknown;
}

interface FIBQuestion {
  id: string;
  type: string;
  title?: string;
  difficulty?: string;
  content: FIBQuestionContent;
  [key: string]: unknown;
}

interface TestData {
  id: string;
  title: string;
  description?: string;
  subject?: string;
  difficulty?: string;
  time_limit_minutes?: number;
}

interface TestSession {
  sessionId: string;
  test: TestData;
  questions: FIBQuestion[];
  timeLimit: number; // seconds
}

interface FIBState {
  cur: number;
  answers: string[];    // indexed by blank position
  flags: boolean[];
  seconds: number;
  done: boolean;
}

// ─────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────

export interface FillInTheBlankProps {
  testId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialSession?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onComplete: (result: any) => void;
  onCancel: () => void;
}

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

const RING_LEN = 2 * Math.PI * 11;

function fmtTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m + ':' + String(sec).padStart(2, '0');
}

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

const FillInTheBlank: React.FC<FillInTheBlankProps> = ({
  testId,
  initialSession,
  onComplete: _onComplete,
  onCancel,
}) => {
  const { user: authUser } = useAuth();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

  const getCurrentUser = () => {
    if (authUser) return authUser;
    try {
      const s = localStorage.getItem('cleveroo_user');
      if (s) return JSON.parse(s);
    } catch (e) {}
    return null;
  };
  const currentUser = getCurrentUser();

  // ── localStorage key ──
  const LS_KEY = `cleveroo_cloze_${testId}`;

  // ── Loading / error ──
  const [session, setSession] = useState<TestSession | null>(
    (initialSession as TestSession) || null
  );
  const [loading, setLoading] = useState(!initialSession);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ── Results overlay ──
  const [result, setResult] = useState<{
    score: number;
    correct: number;
    total: number;
    timeUsed: number;
  } | null>(null);

  // ── Toast ──
  const [toastMsg, setToastMsg] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Per-focused-blank UI state ──
  const [hintOpen, setHintOpen] = useState(false);

  // ── Test start time ──
  const testStartTimeRef = useRef<Date | null>(null);

  // ── Input refs (one per blank position) ──
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // ─────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────

  const loadFIBState = useCallback(
    (total: number, timeLimit: number): FIBState => {
      try {
        const raw = localStorage.getItem(LS_KEY);
        if (raw) {
          const s: FIBState = JSON.parse(raw);
          if (
            s &&
            Array.isArray(s.answers) &&
            s.answers.length === total &&
            Array.isArray(s.flags) &&
            s.flags.length === total
          ) {
            return s;
          }
        }
      } catch (e) {}
      return {
        cur: 0,
        answers: Array(total).fill(''),
        flags: Array(total).fill(false),
        seconds: timeLimit,
        done: false,
      };
    },
    [LS_KEY]
  );

  const [fibState, setFIBState] = useState<FIBState>({
    cur: 0,
    answers: [],
    flags: [],
    seconds: 0,
    done: false,
  });

  const saveStateRef = useRef<FIBState>(fibState);
  useEffect(() => {
    saveStateRef.current = fibState;
  }, [fibState]);

  const persistState = useCallback(
    (s: FIBState) => {
      localStorage.setItem(LS_KEY, JSON.stringify(s));
    },
    [LS_KEY]
  );

  // ─────────────────────────────────────────────
  // FETCH SESSION
  // ─────────────────────────────────────────────

  useEffect(() => {
    if (initialSession) {
      setSession(initialSession);
      setLoading(false);
      return;
    }
    if (!currentUser?.id) {
      setLoadError('User not authenticated');
      setLoading(false);
      return;
    }
    setLoading(true);
    axios
      .get(
        `${API_BASE_URL}/api/students/${currentUser.id}/tests/${testId}/start`,
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: false,
        }
      )
      .then((res) => {
        if (res.data.success) {
          setSession(res.data.data);
        } else {
          setLoadError(res.data.error || 'Failed to start test');
        }
      })
      .catch((err) => {
        setLoadError(
          err.response?.data?.error || err.message || 'Failed to start test'
        );
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId]);

  // ── Initialise FIB state once session is available ──
  useEffect(() => {
    if (!session) return;
    const total = session.questions.length;
    const timeLimit = session.timeLimit;
    const s = loadFIBState(total, timeLimit);
    setFIBState(s);
    testStartTimeRef.current = new Date();
    inputRefs.current = Array(total).fill(null);
  }, [session, loadFIBState]);

  // ─────────────────────────────────────────────
  // CLOZE STRUCTURE
  // Build a mapping from blank-position (0-based split index) → question.
  // Primary: use content.blankIndex. Fallback: array order.
  // ─────────────────────────────────────────────

  const buildBlankMap = useCallback(
    (questions: FIBQuestion[]): FIBQuestion[] => {
      // map[blankPos] = question
      const map: FIBQuestion[] = [];
      questions.forEach((q) => {
        const bi =
          typeof q.content?.blankIndex === 'number'
            ? q.content.blankIndex
            : null;
        if (bi !== null) {
          map[bi] = q;
        }
      });
      // Fill any gaps with array-order questions (fallback)
      questions.forEach((q, i) => {
        if (!map[i]) map[i] = q;
      });
      return map;
    },
    []
  );

  // Template comes from the first question's passage.template
  const template =
    session && session.questions.length > 0
      ? session.questions[0].content?.passage?.template || ''
      : '';

  const passageTitle =
    session && session.questions.length > 0
      ? session.questions[0].content?.passage?.title || ''
      : '';

  // Split on %% to get text segments; blanks sit between segments i and i+1
  const segments = template ? template.split('%%') : [];
  const blankCount = Math.max(0, segments.length - 1);

  // blankMap[i] = question for blank position i
  const blankMap = session ? buildBlankMap(session.questions) : [];

  // ─────────────────────────────────────────────
  // TOAST
  // ─────────────────────────────────────────────

  const showToast = useCallback((msg: string) => {
    setToastMsg(msg);
    setToastVisible(true);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastVisible(false), 2200);
  }, []);

  // ─────────────────────────────────────────────
  // FOCUS / CURRENT
  // ─────────────────────────────────────────────

  const setCurrent = useCallback(
    (i: number) => {
      setFIBState((prev) => {
        if (prev.cur === i) return prev;
        const next = { ...prev, cur: i };
        persistState(next);
        return next;
      });
      // Close hint when switching blanks
      setHintOpen(false);
    },
    [persistState]
  );

  const focusBlank = useCallback(
    (i: number) => {
      const clamped = Math.max(0, Math.min(blankCount - 1, i));
      const el = inputRefs.current[clamped];
      if (el) el.focus();
    },
    [blankCount]
  );

  // ─────────────────────────────────────────────
  // INPUT HANDLERS
  // ─────────────────────────────────────────────

  const handleInput = useCallback(
    (blankPos: number, value: string) => {
      setFIBState((prev) => {
        const next = {
          ...prev,
          answers: [...prev.answers],
        };
        next.answers[blankPos] = value;
        persistState(next);
        return next;
      });
    },
    [persistState]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, blankPos: number) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (blankPos < blankCount - 1) {
          focusBlank(blankPos + 1);
        } else {
          submitRef.current?.();
        }
      }
    },
    [blankCount, focusBlank]
  );

  // ─────────────────────────────────────────────
  // FLAG / HINT / CLEAR
  // ─────────────────────────────────────────────

  const toggleFlag = useCallback(() => {
    setFIBState((prev) => {
      const next = { ...prev, flags: [...prev.flags] };
      next.flags[prev.cur] = !next.flags[prev.cur];
      persistState(next);
      showToast(next.flags[prev.cur] ? `Blank ${prev.cur + 1} flagged` : 'Flag removed');
      return next;
    });
  }, [persistState, showToast]);

  const clearAll = useCallback(async () => {
    if (!(await confirm({
      title: 'Clear all answers?',
      body: 'This wipes everything you typed in this passage.',
      confirmLabel: 'Clear all',
      danger: true,
    }))) return;
    setFIBState((prev) => {
      const next = {
        ...prev,
        answers: Array(prev.answers.length).fill(''),
      };
      persistState(next);
      return next;
    });
    // Also clear the input DOM values
    inputRefs.current.forEach((el) => {
      if (el) el.value = '';
    });
    showToast('All answers cleared');
  }, [persistState, showToast, confirm]);

  // ─────────────────────────────────────────────
  // TIMER
  // ─────────────────────────────────────────────

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const submitRef = useRef<((auto?: boolean) => void) | null>(null);

  useEffect(() => {
    if (!session || fibState.done || fibState.seconds <= 0 || fibState.answers.length === 0)
      return;

    timerRef.current = setInterval(() => {
      setFIBState((prev) => {
        if (prev.done) return prev;
        if (prev.seconds <= 0) {
          if (timerRef.current) clearInterval(timerRef.current);
          submitRef.current?.(true);
          return prev;
        }
        const next = { ...prev, seconds: prev.seconds - 1 };
        if (next.seconds % 5 === 0) persistState(next);
        return next;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, fibState.done, fibState.answers.length === 0]);

  // ─────────────────────────────────────────────
  // SUBMIT
  // ─────────────────────────────────────────────

  const submit = useCallback(
    async (auto?: boolean) => {
      if (!session || !currentUser?.id) return;
      const cur = saveStateRef.current;
      const answeredCount = cur.answers.filter((a) => (a || '').trim() !== '').length;
      const unanswered = blankCount - answeredCount;

      if (!auto && unanswered > 0 && !cur.done) {
        const ok = await confirm({
          title: 'Submit with empty blanks?',
          body: `${unanswered} blank${unanswered > 1 ? 's are' : ' is'} still empty.`,
          confirmLabel: 'Submit anyway',
          cancelLabel: 'Keep filling',
        });
        if (!ok) return;
      }

      setFIBState((prev) => {
        const next = { ...prev, done: true };
        persistState(next);
        return next;
      });
      if (timerRef.current) clearInterval(timerRef.current);
      setIsSubmitting(true);
      setSubmitError(null);

      try {
        // Map blank-position answers back to their questions for submit
        const answersArray = session.questions.map((q) => {
          const bi =
            typeof q.content?.blankIndex === 'number'
              ? q.content.blankIndex
              : session.questions.indexOf(q);
          return {
            questionId: q.id,
            answer: cur.answers[bi] ?? '',
            timeSpent: 0,
          };
        });

        const startTime =
          testStartTimeRef.current?.toISOString() ?? new Date().toISOString();

        const res = await axios.post(
          `${API_BASE_URL}/api/students/${currentUser.id}/tests/${testId}/submit`,
          {
            sessionId: session.sessionId,
            answers: answersArray,
            startTime,
          },
          {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: false,
          }
        );

        if (res.data.success) {
          const data = res.data.data;
          const secondsLeft = saveStateRef.current.seconds;
          const timeUsed = session.timeLimit - secondsLeft;
          setResult({
            score: data.score?.percentage ?? 0,
            correct: data.score?.correct ?? 0,
            total: data.score?.total ?? blankCount,
            timeUsed,
          });
          localStorage.removeItem(LS_KEY);
        } else {
          setSubmitError(res.data.error || 'Failed to submit');
        }
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { error?: string } }; message?: string };
        setSubmitError(
          axiosErr.response?.data?.error || axiosErr.message || 'Failed to submit'
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, currentUser?.id, testId, API_BASE_URL, LS_KEY, blankCount, persistState, confirm]
  );

  useEffect(() => {
    submitRef.current = submit;
  }, [submit]);

  // ─────────────────────────────────────────────
  // KEYBOARD SHORTCUTS (global, when not typing)
  // ─────────────────────────────────────────────

  useEffect(() => {
    if (!session) return;
    const handler = (e: KeyboardEvent) => {
      if (result) return;
      const target = e.target as HTMLElement;
      const isTyping = target && target.classList && target.classList.contains('fib-blank');
      if (isTyping) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        focusBlank(fibState.cur + 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        focusBlank(fibState.cur - 1);
      } else if (e.key.toLowerCase() === 'f') {
        toggleFlag();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, fibState.cur, result, focusBlank, toggleFlag]);

  // ─────────────────────────────────────────────
  // RESULT TITLE / SUBTITLE
  // ─────────────────────────────────────────────

  const getResultText = (pct: number) => {
    const firstName = ((currentUser as { name?: string })?.name || '').split(' ')[0] || '';
    if (pct >= 80)
      return {
        title: firstName ? `Brilliant, ${firstName}!` : 'Brilliant!',
        sub: 'Your spelling and grammar are right on the money.',
      };
    if (pct >= 50)
      return {
        title: firstName ? `Nice work, ${firstName}!` : 'Nice work!',
        sub: "Solid effort — let's review the tricky words together.",
      };
    return {
      title: firstName ? `Good hustle, ${firstName}!` : 'Good hustle!',
      sub: "Every word you practise sticks a little better. Let's debrief.",
    };
  };

  // ─────────────────────────────────────────────
  // DERIVED STATS
  // ─────────────────────────────────────────────

  const answeredCount = fibState.answers.filter((a) => (a || '').trim() !== '').length;
  const flaggedCount = fibState.flags.filter(Boolean).length;
  const pct = blankCount > 0 ? Math.round((answeredCount / blankCount) * 100) : 0;

  const timeLimit = session?.timeLimit ?? 1;
  const frac = Math.max(0, fibState.seconds / timeLimit);
  const ringOffset = RING_LEN * (1 - frac);

  const timerClass = [
    'fib-timer',
    fibState.seconds <= 60
      ? 'is-crit'
      : fibState.seconds <= 5 * 60
      ? 'is-warn'
      : '',
  ]
    .filter(Boolean)
    .join(' ');

  const curQ = blankMap[fibState.cur];
  const curHint = curQ?.content?.hint || '';
  const curCue = curQ?.content?.cue || '';
  const isCurFlagged = fibState.flags[fibState.cur] ?? false;
  const isCurAnswered = (fibState.answers[fibState.cur] || '').trim() !== '';

  // ─────────────────────────────────────────────
  // LOADING / ERROR
  // ─────────────────────────────────────────────

  if (loading) {
    return (
      <div className="fib-root">
        <div className="fib-loading">
          <div className="fib-loading__spinner" />
          <span className="fib-loading__text">Loading fill-in-the-blank…</span>
        </div>
      </div>
    );
  }

  if (loadError || !session) {
    return (
      <div className="fib-root">
        <div className="fib-error">
          <h2>Couldn't load the test</h2>
          <p>{loadError || 'Unknown error'}</p>
          <button className="fib-btn fib-btn--ghost" onClick={onCancel}>
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────

  const isLastBlank = fibState.cur === blankCount - 1;

  return (
    <div className="fib-root">

      {/* ── APP BAR ── */}
      <header className="fib-bar">
        <div className="fib-bar__inner">
          <span className="fib-bar__brand">
            <img src={logoUrl} alt="Cleveroo" />
            <b>
              cleve<span className="fib-r">r</span>
              <span className="fib-o">oo</span>
            </b>
          </span>
          <div className="fib-bar__divider" />
          <div className="fib-bar__test">
            <h1>{session.test.title}</h1>
            <small>Complete the passage · auto-saved as you go</small>
          </div>
          <div className="fib-bar__right">
            <div className={timerClass}>
              <svg className="fib-timer__ring" viewBox="0 0 26 26">
                <circle className="fib-trk" cx="13" cy="13" r="11" />
                <circle
                  className="fib-val"
                  cx="13"
                  cy="13"
                  r="11"
                  style={{
                    strokeDasharray: RING_LEN,
                    strokeDashoffset: ringOffset,
                  }}
                />
              </svg>
              <span className="fib-timer__txt">
                <span className="fib-timer__num">{fmtTime(fibState.seconds)}</span>
                <span className="fib-timer__lbl">remaining</span>
              </span>
            </div>
            <SoundMuteToggle />
            <button
              className="fib-bar__exit"
              onClick={async () => {
                if (await confirm({
                  title: 'Exit the activity?',
                  body: 'Your answers are saved — you can come back and finish later.',
                  confirmLabel: 'Exit',
                  cancelLabel: 'Keep going',
                })) {
                  onCancel();
                }
              }}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <path d="m16 17 5-5-5-5" />
                <path d="M21 12H9" />
              </svg>
              Exit
            </button>
          </div>
        </div>
      </header>

      {/* ── MAIN SHELL ── */}
      <main className="fib-shell">

        {/* ── QUESTION CARD ── */}
        <section className="fib-qcard">
          <div className="fib-qcard__top">
            <span className="fib-tag fib-tag--type">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 7V5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v2" />
                <path d="M9 20h6" />
                <path d="M12 4v16" />
              </svg>
              Fill in the blank
            </span>
            <span className="fib-tag fib-tag--secondary">
              {passageTitle || 'Cloze passage'}
            </span>
            <span className="fib-qcard__qno">
              Blank <b>{fibState.cur + 1}</b> of <span>{blankCount}</span>
            </span>
          </div>

          <h2 className="fib-qcard__title">Complete the passage</h2>
          <p className="fib-qcard__instr">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m18 2 4 4-14 14H4v-4z" />
              <path d="m14.5 5.5 4 4" />
            </svg>
            <span>
              Read the passage, then type the correct word into each numbered blank.
              {blankMap.some((q) => q?.content?.cue) && (
                <>
                  {' '}Purple words like <em className="fib-cue" style={{ marginLeft: 0 }}>(travel)</em> are
                  base words — change them to fit the sentence, don&rsquo;t copy them as-is.
                </>
              )}
            </span>
          </p>

          {/* ── CLOZE BLANKBOX ── */}
          <div className="fib-blankbox">
            <div className="fib-blankline fib-cloze">
              {segments.map((seg, i) => (
                <React.Fragment key={i}>
                  {seg}
                  {i < segments.length - 1 && (() => {
                    const q = blankMap[i];
                    const val = fibState.answers[i] ?? '';
                    const isFilled = val.trim() !== '';
                    const isCurrent = fibState.cur === i;
                    const cue = q?.content?.cue || '';
                    return (
                      <span className="fib-blankwrap">
                        <span className="fib-blanknum">{i + 1}</span>
                        <input
                          ref={(el) => { inputRefs.current[i] = el; }}
                          type="text"
                          id={`fib-blank-${i}`}
                          className={`fib-blank${isFilled ? ' is-filled' : ''}${isCurrent ? '' : ''}`}
                          defaultValue={val}
                          autoComplete="off"
                          autoCapitalize="off"
                          spellCheck={false}
                          aria-label={`Blank ${i + 1}`}
                          onFocus={() => { playSelect(); setCurrent(i); }}
                          onInput={(e) =>
                            handleInput(i, (e.target as HTMLInputElement).value)
                          }
                          onKeyDown={(e) => handleKeyDown(e, i)}
                        />
                        {cue && (
                          <span className="fib-cue">({cue})</span>
                        )}
                      </span>
                    );
                  })()}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* ── HINT ROW ── */}
          <div className="fib-hintrow">
            <div className={`fib-hint${hintOpen ? ' is-open' : ''}`}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 18h6" />
                <path d="M10 22h4" />
                <path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.2 1 2h6c0-.8.4-1.5 1-2A7 7 0 0 0 12 2z" />
              </svg>
              <span>{curHint}</span>
            </div>
          </div>

          {/* ── CARD FOOTER ── */}
          <div className="fib-qcard__foot">
            {/* Flag */}
            <button
              className={`fib-linkbtn fib-flagbtn${isCurFlagged ? ' is-on' : ''}`}
              onClick={toggleFlag}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                <line x1="4" y1="22" x2="4" y2="15" />
              </svg>
              {isCurFlagged ? 'Flagged for review' : 'Flag this blank'}
            </button>

            {/* Show/Hide hint — hidden if no hint for current blank */}
            {curHint && (
              <button
                className={`fib-linkbtn fib-hintbtn${hintOpen ? ' is-on' : ''}`}
                onClick={() => setHintOpen((v) => !v)}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 18h6" />
                  <path d="M10 22h4" />
                  <path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.2 1 2h6c0-.8.4-1.5 1-2A7 7 0 0 0 12 2z" />
                </svg>
                {hintOpen ? 'Hide hint' : 'Show hint'}
              </button>
            )}

            {/* Clear all */}
            <button className="fib-linkbtn" onClick={clearAll}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 6h18" />
                <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
                <path d="m19 6-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              </svg>
              Clear all
            </button>

            {/* Answer saved hint */}
            <span
              className="fib-qcard__hint"
              style={{ visibility: isCurAnswered ? 'visible' : 'hidden' }}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
              Answer saved
            </span>

            {/* Current cue label (shown in footer too if present) */}
            {curCue && (
              <span
                style={{
                  fontSize: 12,
                  color: 'var(--grape)',
                  fontWeight: 600,
                  fontStyle: 'italic',
                  marginLeft: 4,
                }}
              >
                base: ({curCue})
              </span>
            )}
          </div>

          {submitError && (
            <div
              style={{
                color: '#E26B4F',
                fontWeight: 600,
                fontSize: 14,
                padding: '8px 0',
              }}
            >
              {submitError}
            </div>
          )}
        </section>

        {/* ── RIGHT RAIL ── */}
        <aside className="fib-rail">
          <div className="fib-panel">
            {/* Progress head */}
            <div className="fib-panel__head">
              <span className="fib-ico">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 18l5-6 4 4 8-10" />
                </svg>
              </span>
              Your progress
              <span className="fib-pct">{pct}%</span>
            </div>
            <div className="fib-pbar">
              <div className="fib-pbar__fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="fib-pmeta">
              <span className="fib-k">
                Answered <b>{answeredCount}</b> of <span>{blankCount}</span>
              </span>
              {flaggedCount > 0 && (
                <span style={{ color: 'var(--coral)', fontWeight: 600 }}>
                  {flaggedCount} flagged
                </span>
              )}
            </div>

            {/* Blank map */}
            <div className="fib-navsec">
              <div className="fib-navsec__div" />
              <div className="fib-navsec__head">
                <span>Blank map</span>
                <span className="fib-navsec__hint">Tap to jump</span>
              </div>
              <div className="fib-legend">
                <span>
                  <i className="fib-li-ans" />
                  Answered
                </span>
                <span>
                  <i className="fib-li-cur" />
                  Current
                </span>
                <span>
                  <i className="fib-li-flag" />
                  Flagged
                </span>
                <span>
                  <i className="fib-li-todo" />
                  To do
                </span>
              </div>
              <div className="fib-grid">
                {Array.from({ length: blankCount }, (_, i) => {
                  const isAns = (fibState.answers[i] || '').trim() !== '';
                  const isCur = i === fibState.cur;
                  const isFl = fibState.flags[i];
                  let cls = 'fib-cell';
                  if (isCur) cls += ' is-current';
                  else if (isAns) cls += ' is-answered';
                  if (isFl) cls += ' is-flagged';
                  return (
                    <button
                      key={i}
                      className={cls}
                      onClick={() => focusBlank(i)}
                    >
                      {i + 1}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </aside>
      </main>

      {/* ── STICKY NAV ── */}
      <nav className="fib-navbar">
        <div className="fib-navbar__inner">
          <button
            className="fib-btn fib-btn--ghost"
            disabled={fibState.cur === 0}
            onClick={() => focusBlank(fibState.cur - 1)}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5m6-6-6 6 6 6" />
            </svg>
            <span className="lbl">Previous</span>
          </button>

          <div className="fib-navbar__dots">
            {Array.from({ length: blankCount }, (_, i) => {
              const isAns = (fibState.answers[i] || '').trim() !== '';
              const isCur = i === fibState.cur;
              const isFl = fibState.flags[i];
              let cls = 'fib-navbar__dot';
              if (isCur) cls += ' is-current';
              else if (isAns) cls += ' is-answered';
              else if (isFl) cls += ' is-flagged';
              return <span key={i} className={cls} />;
            })}
          </div>

          {isLastBlank ? (
            <button
              className="fib-btn fib-btn--primary fib-btn--submit"
              onClick={() => submit()}
              disabled={isSubmitting}
            >
              <span className="lbl">{isSubmitting ? 'Submitting…' : 'Submit answers'}</span>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </button>
          ) : (
            <button
              className="fib-btn fib-btn--primary"
              onClick={() => focusBlank(fibState.cur + 1)}
            >
              <span className="lbl">Next blank</span>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12h14m-6-6 6 6-6 6" />
              </svg>
            </button>
          )}
        </div>
      </nav>

      {/* ── RESULTS OVERLAY ── */}
      {result &&
        (() => {
          const { title, sub } = getResultText(result.score);
          return (
            <div className="fib-scrim is-open">
              <div className="fib-dialog">
                <div className="fib-dialog__badge">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M6 9V6a3 3 0 0 1 3-3h6a3 3 0 0 1 3 3v3" />
                    <path d="M4 9h16v3a8 8 0 1 1-16 0z" />
                    <path d="M12 17v4M8 21h8" />
                  </svg>
                </div>
                <h2>{title}</h2>
                <p>{sub}</p>
                <div className="fib-dialog__score">
                  <div className="fib-scorebox">
                    <b>{result.score}%</b>
                    <small>Score</small>
                  </div>
                  <div className="fib-scorebox alt">
                    <b>
                      {result.correct}/{result.total}
                    </b>
                    <small>Correct</small>
                  </div>
                  <div className="fib-scorebox alt2">
                    <b>{fmtTime(result.timeUsed)}</b>
                    <small>Time used</small>
                  </div>
                </div>
                <div className="fib-dialog__actions">
                  <button
                    className="fib-btn fib-btn--ghost"
                    onClick={() => {
                      setResult(null);
                      focusBlank(0);
                    }}
                  >
                    Review answers
                  </button>
                  <button className="fib-btn fib-btn--primary" onClick={onCancel}>
                    Back to dashboard
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* ── TOAST ── */}
      <div className={`fib-toast${toastVisible ? ' is-show' : ''}`}>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 6 9 17l-5-5" />
        </svg>
        <span>{toastMsg}</span>
      </div>
      {confirmDialog}
    </div>
  );
};

export default FillInTheBlank;
